import React, { useState, useEffect, useRef } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Plus, Edit, Trash2, Package, Search, 
  DollarSign, Archive, ImageIcon, X, Tag, FolderPlus, Upload
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Image compression constants to avoid nginx 413 errors
const TARGET_UPLOAD_MAX_BYTES = 700 * 1024; // ~700KB (conservative for 1MB Nginx limit)
const TARGET_IMAGE_MAX_DIMENSION = 1600;

const loadImageElement = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    resolve(img);
  };
  img.onerror = (err) => {
    URL.revokeObjectURL(url);
    reject(err);
  };
  img.src = url;
});

const canvasToBlob = (canvas, type, quality) => new Promise((resolve) => {
  canvas.toBlob((blob) => resolve(blob), type, quality);
});

const compressImageIfNeeded = async (file) => {
  // Keep gifs as-is (canvas conversion breaks animation).
  if (!file || file.type === 'image/gif') return { file, changed: false };
  if (file.size <= TARGET_UPLOAD_MAX_BYTES) return { file, changed: false };

  try {
    const img = await loadImageElement(file);
    const srcWidth = img.naturalWidth || img.width;
    const srcHeight = img.naturalHeight || img.height;
    if (!srcWidth || !srcHeight) return { file, changed: false };

    const scale = Math.min(1, TARGET_IMAGE_MAX_DIMENSION / Math.max(srcWidth, srcHeight));
    const outWidth = Math.max(1, Math.round(srcWidth * scale));
    const outHeight = Math.max(1, Math.round(srcHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = outWidth;
    canvas.height = outHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return { file, changed: false };

    // White background to avoid black fill when converting transparent PNG -> JPEG.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, outWidth, outHeight);
    ctx.drawImage(img, 0, 0, outWidth, outHeight);

    // Iteratively lower JPEG quality until we hit target size.
    const outType = 'image/jpeg';
    let quality = 0.80;
    let blob = await canvasToBlob(canvas, outType, quality);
    while (blob && blob.size > TARGET_UPLOAD_MAX_BYTES && quality > 0.3) {
      quality -= 0.08;
      blob = await canvasToBlob(canvas, outType, quality);
    }

    if (!blob) return { file, changed: false };
    if (blob.size > file.size) return { file, changed: false };

    const baseName = (file.name || 'upload').replace(/\.[^/.]+$/, '');
    const newFile = new File([blob], `${baseName}.jpg`, { type: outType });
    return { file: newFile, changed: true };
  } catch {
    return { file, changed: false };
  }
};

const DEFAULT_CATEGORIES = [
  'Refreshments',
  'Snacks', 
  'Equipment',
  'Accessories',
  'Apparel',
  'Services',
  'Other'
];

const AdminAddOnsPage = () => {
  const { token, hasRole } = useAdminAuth();
  const { theme, isDarkMode } = useAdminTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  
  // Categories management
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  
  const [form, setForm] = useState({
    name: '',
    description: '',
    category: '',
    price: 0,
    cost: 0,
    available_quantity: 10,
    image: '',
    sku: '',
    is_active: true
  });
  
  // Image upload states
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchItems();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchItems = async () => {
    try {
      const res = await fetch(`${API}/admin/pos/items`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setItems(await res.json());
      }
    } catch (error) {
      console.error('Failed to fetch items:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/admin/pos/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.length > 0) {
          setCategories(data.map(c => c.name));
        }
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const addCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Category name is required');
      return;
    }
    if (categories.includes(newCategoryName.trim())) {
      toast.error('Category already exists');
      return;
    }
    
    try {
      const res = await fetch(`${API}/admin/pos/categories`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newCategoryName.trim() })
      });
      
      if (res.ok) {
        toast.success('Category added');
        setCategories(prev => [...prev, newCategoryName.trim()]);
        setNewCategoryName('');
      } else {
        toast.error('Failed to add category');
      }
    } catch (error) {
      toast.error('Error adding category');
    }
  };

  const updateCategory = async (oldName) => {
    if (!editCategoryName.trim()) {
      toast.error('Category name is required');
      return;
    }
    if (editCategoryName.trim() !== oldName && categories.includes(editCategoryName.trim())) {
      toast.error('Category already exists');
      return;
    }
    
    try {
      const res = await fetch(`${API}/admin/pos/categories/${encodeURIComponent(oldName)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: editCategoryName.trim() })
      });
      
      if (res.ok) {
        toast.success('Category updated');
        setCategories(prev => prev.map(c => c === oldName ? editCategoryName.trim() : c));
        setEditingCategory(null);
        setEditCategoryName('');
        fetchItems(); // Refresh items to reflect category name change
      } else {
        toast.error('Failed to update category');
      }
    } catch (error) {
      toast.error('Error updating category');
    }
  };

  const deleteCategory = async (name) => {
    const itemsInCategory = items.filter(i => i.category === name).length;
    if (itemsInCategory > 0) {
      toast.error(`Cannot delete category with ${itemsInCategory} item(s). Move items first.`);
      return;
    }
    
    try {
      const res = await fetch(`${API}/admin/pos/categories/${encodeURIComponent(name)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Category deleted');
        setCategories(prev => prev.filter(c => c !== name));
      } else {
        toast.error('Failed to delete category');
      }
    } catch (error) {
      toast.error('Error deleting category');
    }
  };

  const saveItem = async () => {
    if (!form.name || form.price <= 0) {
      toast.error('Name and price are required');
      return;
    }

    try {
      setUploading(true);
      
      // Use FormData for multipart upload when we have an image file
      if (imageFile || editingItem) {
        const formData = new FormData();
        formData.append('name', form.name);
        formData.append('description', form.description || '');
        formData.append('category', form.category || 'Equipment');
        formData.append('price', form.price.toString());
        formData.append('cost', (form.cost || 0).toString());
        formData.append('available_quantity', (form.available_quantity || 0).toString());
        formData.append('sku', form.sku || '');
        formData.append('is_active', form.is_active ? 'true' : 'false');
        
        if (imageFile) {
          // Compress image if needed to avoid nginx 413 errors
          const { file: uploadFile, changed } = await compressImageIfNeeded(imageFile);
          if (changed) {
            toast.info(`Image compressed from ${(imageFile.size / 1024).toFixed(0)}KB to ${(uploadFile.size / 1024).toFixed(0)}KB`);
          }
          formData.append('image', uploadFile);
        }
        
        const url = editingItem 
          ? `${API}/admin/pos/items/${editingItem.item_id}/upload`
          : `${API}/admin/pos/items/upload`;
        
        const res = await fetch(url, {
          method: editingItem ? 'PUT' : 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        if (res.ok) {
          toast.success(editingItem ? 'Item updated' : 'Item created');
          setShowModal(false);
          setEditingItem(null);
          resetForm();
          fetchItems();
        } else {
          const errorData = await res.json().catch(() => ({}));
          toast.error(errorData.detail || 'Failed to save item');
        }
      } else {
        // No image, use regular JSON POST
        const url = editingItem 
          ? `${API}/admin/pos/items/${editingItem.item_id}`
          : `${API}/admin/pos/items`;
        
        const res = await fetch(url, {
          method: editingItem ? 'PUT' : 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(form)
        });

        if (res.ok) {
          toast.success(editingItem ? 'Item updated' : 'Item created');
          setShowModal(false);
          setEditingItem(null);
          resetForm();
          fetchItems();
        } else {
          toast.error('Failed to save item');
        }
      }
    } catch (error) {
      toast.error('Error saving item');
    } finally {
      setUploading(false);
    }
  };

  const deleteItem = async (itemId) => {
    if (!confirm('Are you sure you want to delete this item?')) return;
    
    try {
      const res = await fetch(`${API}/admin/pos/items/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Item deleted');
        fetchItems();
      }
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      category: categories.length > 0 ? categories[0] : '',
      price: 0,
      cost: 0,
      available_quantity: 10,
      image: '',
      sku: '',
      is_active: true
    });
    setImageFile(null);
    setImagePreview('');
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name || '',
      description: item.description || '',
      category: item.category || 'Refreshments',
      price: item.price || 0,
      cost: item.cost || 0,
      available_quantity: item.available_quantity || 0,
      image: item.image || '',
      sku: item.sku || '',
      is_active: item.is_active !== false
    });
    setImageFile(null);
    setImagePreview(item.image || '');
    setShowModal(true);
  };
  
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Accept standard images and HEIC/HEIF files
      const isHeic = file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
      if (!isHeic && !file.type.startsWith('image/')) {
        toast.error('Please select an image file (JPG, PNG, GIF, HEIC)');
        return;
      }
      if (file.size > 14 * 1024 * 1024) {
        toast.error('Image size must be less than 14MB');
        return;
      }
      setImageFile(file);
      
      // For HEIC files, show a placeholder since browsers can't preview them
      if (isHeic) {
        setImagePreview(''); // Clear preview, we'll show filename instead
        toast.success(`HEIC file selected: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
      } else {
        const previewUrl = URL.createObjectURL(file);
        setImagePreview(previewUrl);
      }
    }
  };
  
  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setForm(prev => ({ ...prev, image: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name?.toLowerCase().includes(search.toLowerCase()) ||
                          item.description?.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !filterCategory || item.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-2xl font-bold ${theme.text}`}>Add-ons Management</h1>
            <p className={`${theme.textMuted} mt-1`}>Manage POS items for sale</p>
          </div>
          {hasRole(['super_admin', 'manager']) && (
            <div className="flex gap-2">
              <Button 
                onClick={() => setShowCategoriesModal(true)} 
                variant="outline"
                className={isDarkMode ? 'border-slate-600 text-slate-200 hover:text-white hover:bg-slate-700' : 'border-gray-300 text-gray-700 hover:text-gray-900'}
                data-testid="manage-categories-btn"
              >
                <Tag className="h-4 w-4 mr-2" /> Manage Categories
              </Button>
              <Button 
                onClick={() => { resetForm(); setEditingItem(null); setShowModal(true); }} 
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="add-item-btn"
              >
                <Plus className="h-4 w-4 mr-2" /> Add Item
              </Button>
            </div>
          )}
        </div>

        {/* Filters */}
        <Card className={isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'}>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                  <Input
                    placeholder="Search items..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className={`pl-10 ${theme.input}`}
                  />
                </div>
              </div>
              <div className="min-w-[150px]">
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className={isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'} flex items-center justify-center`}>
                  <Package className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className={`text-sm ${theme.textMuted}`}>Total Items</p>
                  <p className={`text-xl font-bold ${theme.text}`}>{items.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'} flex items-center justify-center`}>
                  <Archive className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <p className={`text-sm ${theme.textMuted}`}>In Stock</p>
                  <p className={`text-xl font-bold ${theme.text}`}>
                    {items.filter(i => (i.available_quantity || 0) > 0).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${isDarkMode ? 'bg-rose-500/20' : 'bg-rose-100'} flex items-center justify-center`}>
                  <Archive className="h-5 w-5 text-rose-500" />
                </div>
                <div>
                  <p className={`text-sm ${theme.textMuted}`}>Out of Stock</p>
                  <p className={`text-xl font-bold ${theme.text}`}>
                    {items.filter(i => (i.available_quantity || 0) <= 0).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Items Grid */}
        <Card className={isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'}>
          <CardHeader>
            <CardTitle className={theme.text}>
              Inventory ({filteredItems.length} items)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="text-center py-12">
                <Package className={`h-12 w-12 ${isDarkMode ? 'text-slate-600' : 'text-gray-400'} mx-auto mb-4`} />
                <p className={theme.textMuted}>No items found</p>
                {hasRole(['super_admin', 'manager']) && (
                  <Button 
                    onClick={() => { resetForm(); setShowModal(true); }}
                    className="mt-4 bg-emerald-600 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" /> Add First Item
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`text-left text-sm ${theme.textMuted} border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                      <th className="pb-3 font-medium">Item</th>
                      <th className="pb-3 font-medium">Category</th>
                      <th className="pb-3 font-medium">Price</th>
                      <th className="pb-3 font-medium">Cost</th>
                      <th className="pb-3 font-medium">Stock</th>
                      <th className="pb-3 font-medium">Status</th>
                      <th className="pb-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item) => (
                      <tr key={item.item_id} className={`border-b ${isDarkMode ? 'border-slate-700/50' : 'border-gray-100'} text-sm`}>
                        <td className="py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'} flex items-center justify-center overflow-hidden`}>
                              {item.image ? (
                                <img src={item.image} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <Package className={`h-5 w-5 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                              )}
                            </div>
                            <div>
                              <p className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-medium`}>{item.name}</p>
                              {item.sku && <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>SKU: {item.sku}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-200 text-gray-700'}`}>
                            {item.category}
                          </span>
                        </td>
                        <td className="py-3 text-emerald-500 font-medium">₱{item.price?.toFixed(2)}</td>
                        <td className={`py-3 ${theme.textMuted}`}>₱{(item.cost || 0).toFixed(2)}</td>
                        <td className="py-3">
                          {item.category === 'Services' || item.name.toLowerCase().includes('ball picker') ? (
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>N/A</span>
                          ) : (
                            <span className={`font-medium ${
                              (item.available_quantity || 0) > 10 
                                ? 'text-emerald-500' 
                                : (item.available_quantity || 0) > 0 
                                  ? 'text-amber-500' 
                                  : 'text-rose-500'
                            }`}>
                              {item.available_quantity || 0}
                            </span>
                          )}
                        </td>
                        <td className="py-3">
                          {item.is_active !== false ? (
                            <span className="px-2 py-1 rounded text-xs bg-emerald-500/20 text-emerald-500">
                              Active
                            </span>
                          ) : (
                            <span className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-slate-500/20 text-slate-400' : 'bg-gray-200 text-gray-500'}`}>
                              Inactive
                            </span>
                          )}
                        </td>
                        <td className="py-3">
                          {hasRole(['super_admin', 'manager']) && (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className={isDarkMode ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}
                                onClick={() => openEditModal(item)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-rose-400 hover:text-rose-300"
                                onClick={() => deleteItem(item.item_id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <Card className={`w-full max-w-lg ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} my-8`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={theme.text}>
                  {editingItem ? 'Edit Item' : 'Add New Item'}
                </CardTitle>
                <Button size="icon" variant="ghost" onClick={() => { setShowModal(false); setEditingItem(null); }} className={theme.textMuted}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={theme.text}>Item Name *</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => {
                        const newName = e.target.value;
                        setForm(prev => ({ 
                          ...prev, 
                          name: newName,
                          // Set stock to 999 for ball picker items (unlimited)
                          available_quantity: newName.toLowerCase().includes('ball picker') ? 999 : prev.available_quantity
                        }));
                      }}
                      className={theme.input}
                      placeholder="e.g., Bottled Water"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <Label className={theme.text}>Description</Label>
                    <textarea
                      value={form.description}
                      onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                      className={`w-full h-20 px-3 py-2 rounded-md border resize-none ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      placeholder="Item description..."
                    />
                  </div>

                  <div>
                    <Label className={theme.text}>Category *</Label>
                    <select
                      value={form.category}
                      onChange={(e) => {
                        const newCategory = e.target.value;
                        setForm(prev => {
                          const updated = { ...prev, category: newCategory };
                          // Auto-set stock to 999 for ball picker items when category changes to Services
                          if (prev.name.toLowerCase().includes('ball picker') && newCategory === 'Services') {
                            updated.available_quantity = 999;
                          }
                          return updated;
                        });
                      }}
                      className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label className={theme.text}>SKU</Label>
                    <Input
                      value={form.sku}
                      onChange={(e) => setForm(prev => ({ ...prev, sku: e.target.value }))}
                      className={theme.input}
                      placeholder="e.g., BW-001"
                    />
                  </div>

                  <div>
                    <Label className={theme.text}>Selling Price (₱) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.price}
                      onChange={(e) => setForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                      className={theme.input}
                    />
                  </div>

                  <div>
                    <Label className={theme.text}>Cost (₱)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.cost}
                      onChange={(e) => setForm(prev => ({ ...prev, cost: parseFloat(e.target.value) || 0 }))}
                      className={theme.input}
                    />
                  </div>

                  {form.category !== 'Services' && !form.name.toLowerCase().includes('ball picker') && (
                    <div>
                      <Label className={theme.text}>Stock Quantity</Label>
                      <Input
                        type="number"
                        value={form.available_quantity}
                        onChange={(e) => setForm(prev => ({ ...prev, available_quantity: parseInt(e.target.value) || 0 }))}
                        className={theme.input}
                      />
                    </div>
                  )}

                  <div>
                    <Label className={theme.text}>Status</Label>
                    <select
                      value={form.is_active ? 'active' : 'inactive'}
                      onChange={(e) => setForm(prev => ({ ...prev, is_active: e.target.value === 'active' }))}
                      className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  <div className="col-span-2">
                    <Label className={theme.text}>Product Image</Label>
                    <div className={`mt-2 border-2 border-dashed rounded-lg p-4 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}>
                      {imageFile ? (
                        // Show HEIC placeholder or image preview
                        <div className="relative">
                          {imageFile.name.toLowerCase().endsWith('.heic') || imageFile.name.toLowerCase().endsWith('.heif') ? (
                            // HEIC placeholder
                            <div className={`w-full h-40 rounded-lg flex flex-col items-center justify-center ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                              <ImageIcon className={`h-16 w-16 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                              <p className={`mt-2 text-sm font-medium ${theme.text}`}>{imageFile.name}</p>
                              <p className={`text-xs ${theme.textMuted}`}>
                                HEIC file ({(imageFile.size / 1024 / 1024).toFixed(2)} MB) - Will be converted to JPEG
                              </p>
                            </div>
                          ) : imagePreview ? (
                            // Regular image preview
                            <img 
                              src={imagePreview} 
                              alt="Preview" 
                              className="w-full h-40 object-contain rounded-lg"
                            />
                          ) : null}
                          <button 
                            type="button"
                            onClick={removeImage}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (imagePreview || form.image) ? (
                        // Existing image from database
                        <div className="relative">
                          <img 
                            src={imagePreview || form.image} 
                            alt="Preview" 
                            className="w-full h-40 object-contain rounded-lg"
                          />
                          <button 
                            type="button"
                            onClick={removeImage}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        // Upload prompt
                        <div 
                          className={`text-center py-6 cursor-pointer ${isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'}`}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <Upload className={`h-10 w-10 mx-auto ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                          <p className={`mt-2 text-sm ${theme.textMuted}`}>Click to upload image</p>
                          <p className={`mt-1 text-xs ${theme.textMuted}`}>PNG, JPG, GIF, HEIC up to 14MB</p>
                        </div>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,.heic,.heif"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                      {!(imagePreview || form.image) && (
                        <Button
                          type="button"
                          variant="outline"
                          className={`w-full mt-2 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <ImageIcon className="h-4 w-4 mr-2" />
                          Select Image
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Profit Margin Preview */}
                {form.price > 0 && form.cost > 0 && (
                  <div className={`p-3 ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-100'} rounded-lg`}>
                    <p className={`text-sm ${theme.textMuted}`}>
                      Profit Margin: 
                      <span className="text-emerald-500 font-medium ml-2">
                        ₱{(form.price - form.cost).toFixed(2)} 
                        ({(((form.price - form.cost) / form.price) * 100).toFixed(1)}%)
                      </span>
                    </p>
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button 
                    variant="outline" 
                    className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}
                    onClick={() => { setShowModal(false); setEditingItem(null); resetForm(); }}
                    disabled={uploading}
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" 
                    onClick={saveItem}
                    disabled={uploading}
                  >
                    {uploading ? (
                      <>
                        <span className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full"></span>
                        Saving...
                      </>
                    ) : (
                      editingItem ? 'Update Item' : 'Add Item'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Categories Modal */}
        {showCategoriesModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <Card className={`w-full max-w-md ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} my-8`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`${theme.text} flex items-center gap-2`}>
                  <Tag className="h-5 w-5 text-emerald-500" />
                  Manage Categories
                </CardTitle>
                <Button size="icon" variant="ghost" onClick={() => setShowCategoriesModal(false)} className={theme.textMuted}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add New Category */}
                <div className="flex gap-2">
                  <Input
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="New category name..."
                    className={`flex-1 ${theme.input}`}
                    onKeyDown={(e) => e.key === 'Enter' && addCategory()}
                  />
                  <Button 
                    onClick={addCategory}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    disabled={!newCategoryName.trim()}
                  >
                    <FolderPlus className="h-4 w-4" />
                  </Button>
                </div>

                {/* Categories List */}
                <div className={`border rounded-lg ${isDarkMode ? 'border-slate-700' : 'border-gray-200'} max-h-64 overflow-y-auto`}>
                  {categories.length === 0 ? (
                    <div className="p-4 text-center">
                      <p className={theme.textMuted}>No categories yet</p>
                    </div>
                  ) : (
                    categories.map((cat, index) => {
                      const itemCount = items.filter(i => i.category === cat).length;
                      return (
                        <div 
                          key={cat} 
                          className={`flex items-center justify-between p-3 ${index !== categories.length - 1 ? `border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}` : ''}`}
                        >
                          {editingCategory === cat ? (
                            <div className="flex-1 flex gap-2 mr-2">
                              <Input
                                value={editCategoryName}
                                onChange={(e) => setEditCategoryName(e.target.value)}
                                className={`flex-1 h-8 ${theme.input}`}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') updateCategory(cat);
                                  if (e.key === 'Escape') { setEditingCategory(null); setEditCategoryName(''); }
                                }}
                                autoFocus
                              />
                              <Button size="sm" className="h-8 bg-emerald-600 text-white" onClick={() => updateCategory(cat)}>
                                Save
                              </Button>
                              <Button size="sm" variant="outline" className="h-8" onClick={() => { setEditingCategory(null); setEditCategoryName(''); }}>
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center gap-2">
                                <Tag className={`h-4 w-4 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                                <span className={theme.text}>{cat}</span>
                                <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>
                                  {itemCount} item{itemCount !== 1 ? 's' : ''}
                                </span>
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className={isDarkMode ? 'text-slate-400 hover:text-white h-8' : 'text-gray-500 hover:text-gray-900 h-8'}
                                  onClick={() => { setEditingCategory(cat); setEditCategoryName(cat); }}
                                >
                                  <Edit className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-rose-400 hover:text-rose-300 h-8"
                                  onClick={() => deleteCategory(cat)}
                                  disabled={itemCount > 0}
                                  title={itemCount > 0 ? 'Cannot delete category with items' : 'Delete category'}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

                <p className={`text-xs ${theme.textMuted}`}>
                  Note: Categories with items cannot be deleted. Move or delete items first.
                </p>

                <div className="flex justify-end pt-2">
                  <Button 
                    variant="outline" 
                    className={isDarkMode ? 'border-slate-600' : 'border-gray-300'}
                    onClick={() => setShowCategoriesModal(false)}
                  >
                    Done
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAddOnsPage;
